import React, { useEffect, useMemo, useState } from "react";

// const API_BASE = "http://localhost:9090/api";
const API_BASE = "https://node-api.mediitacademy.com/api";
const categories = ["All", "NEET", "JEE Main", "Class 8", "Class 7", "Boards + Foundation"];

type Course = { id: number; title?: string; category?: string; duration?: string; students?: number; lessons?: number; price?: number | string; status?: string; progress?: number; color?: string; image_url?: string | null; video_url?: string | null; pdf_url?: string | null; };

const AdminCourses: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); 

  // FORM DATA UPDATED: Teacher removed, Duration added
  // const [formData, setFormData] = useState({ title: "", category: "NEET", duration: "1 Year", price: "", status: "Active", color: "bg-blue-600", description: "", });
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState("");
  const [plans, setPlans] = useState<any[]>([]);
  const [newPlan, setNewPlan] = useState({ id: "Basic", name: "Basic Plan", price: "", duration: "6 Months", planFeatures: "", });
  const [files, setFiles] = useState<{ image: File | null; video: File | null; pdf: File | null }>({ image: null, video: null, pdf: null, });

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await fetch(`${API_BASE}/courses`); 
      const data = await response.json();
      setCourses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => { fetchCourses(); }, []);

  const [formData, setFormData] = useState({ 
      title: "", 
      category: "NEET", 
      duration: "1 Year", 
      price: "", 
      status: "Active", 
      color: "bg-blue-600", 
      description: "", 
      level: "Beginner", 
      tag: "", 
      short_desc: "", 
      seoTitle: "", 
      audience: "", 
      slug: ""
  });

  const resetForm = () => {
    setFormData({ 
      title: "", category: "NEET", duration: "1 Year", price: "", status: "Active", color: "bg-blue-600", description: "",
      level: "Beginner", tag: "", short_desc: "", seoTitle: "", audience: "", slug: "" // Add these
    });
    setFeatures([]);
    setPlans([]);
    setFiles({ image: null, video: null, pdf: null });
    setNewFeature("");
    setNewPlan({ id: "Basic", name: "Basic Plan", price: "", duration: "6 Months", planFeatures: "" });
  };

  const handleAddFeature = () => { if (newFeature.trim() !== "") { setFeatures([...features, newFeature.trim()]); setNewFeature(""); } };
  const handleRemoveFeature = (index: number) => setFeatures(features.filter((_, itemIndex) => itemIndex !== index));
  const handleAddPlan = () => { if (newPlan.price && newPlan.duration && newPlan.planFeatures) { setPlans([...plans, { ...newPlan, features: newPlan.planFeatures.split(",").map((f) => f.trim()).filter(Boolean) }]); setNewPlan({ id: "Basic", name: "Basic Plan", price: "", duration: "6 Months", planFeatures: "" }); } else { alert("Please enter Price, Duration, and Features."); } };
  const handleRemovePlan = (index: number) => setPlans(plans.filter((_, itemIndex) => itemIndex !== index));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => data.append(key, value as string));
    data.append("features", JSON.stringify(features));
    data.append("plans", JSON.stringify(plans));
    if (files.image) data.append("image", files.image);
    if (files.video) data.append("video", files.video);
    if (files.pdf) data.append("pdf", files.pdf);

    try {
      const response = await fetch(`${API_BASE}/courses`, { method: "POST", body: data, });
      const result = await response.json().catch(() => null);
      if (response.ok) {
        alert("Course Outline Created! Now you can create batches for it.");
        setIsModalOpen(false);
        resetForm();
        fetchCourses(); 
      } else alert(result?.error || "Failed to add course.");
    } catch (error) { alert("Server error while creating course."); } finally { setIsSubmitting(false); }
  };

  const filteredCourses = useMemo(() => {
    const text = search.toLowerCase().trim();
    return courses.filter((course) => {
      const matchCat = activeCategory === "All" || course.category === activeCategory;
      const matchSearch = text === "" || `${course.title || ""} ${course.category || ""}`.toLowerCase().includes(text);
      return matchCat && matchSearch;
    });
  }, [courses, activeCategory, search]);

  return (
    <div className="space-y-6 font-['Inter']">
      <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div>
          <p className="mb-2 text-xs font-black uppercase tracking-wide text-blue-600">Master Course Directory</p>
          <h1 className="text-2xl font-black text-slate-950 sm:text-3xl">Manage All Courses</h1>
          <p className="text-sm font-bold text-slate-500 mt-2">Create parent courses here. Teachers and live timings will be assigned in the Batches section.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setIsModalOpen(true)} className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg hover:bg-blue-700">+ Create Parent Course</button>
        </div>
      </section>

      {/* Filter Section */}
      <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2">
            {categories.map((item) => (
              <button key={item} onClick={() => setActiveCategory(item)} className={`rounded-2xl px-4 py-2 text-sm font-black transition ${activeCategory === item ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{item}</button>
            ))}
          </div>
          <div className="flex h-12 w-full items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 xl:max-w-md">
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search courses..." className="w-full bg-transparent text-sm font-semibold outline-none" />
          </div>
        </div>
      </section>

      {/* Courses List */}
      <section className="grid gap-5 xl:grid-cols-2">
        {filteredCourses.map((course) => (
          <div key={course.id} className="relative rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm hover:shadow-xl transition-all">
             <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                <div className="flex gap-4">
                  <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl ${course.color || "bg-blue-600"} text-2xl font-black text-white shadow-lg`}>
                    {course.category ? course.category.slice(0, 1) : "C"}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600">{course.category}</span>
                      <span className={`rounded-full px-3 py-1 text-xs font-black ${course.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}>{course.status}</span>
                    </div>
                    <h2 className="mt-3 text-xl font-black text-slate-950">{course.title}</h2>
                    <p className="mt-1 text-sm font-bold text-slate-500">Duration: {course.duration || "N/A"}</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <MiniInfo label="Total Students" value={(course.students || 0).toLocaleString()} />
                <MiniInfo label="Base Price" value={`₹${course.price || 0}`} />
                <MiniInfo label="Progress" value={`${course.progress || 0}%`} />
                <button className="rounded-2xl border border-slate-200 text-sm font-black text-slate-700 hover:bg-slate-50">Batches</button>
              </div>
          </div>
        ))}
      </section>

      {/* Add Course Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col bg-white rounded-[34px] shadow-2xl overflow-hidden">
            <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-slate-50 p-6 lg:p-8">
              <div>
                <h2 className="text-2xl font-black text-slate-950">Add New Parent Course</h2>
                <p className="mt-1 text-sm font-bold text-slate-500">Define course structure and materials here.</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="h-10 w-10 rounded-full bg-slate-200 font-black hover:bg-red-100 text-slate-600">X</button>
            </div>

             <form id="courseForm" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 lg:p-8 space-y-8">
               <div>
                  <h3 className="mb-4 border-b pb-2 text-sm font-black uppercase tracking-widest text-blue-600">1. Basic Details</h3>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-xs font-black uppercase text-slate-500">Course Title</label>
                      <input required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none focus:border-blue-500" placeholder="e.g. NEET Complete Biology" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-black uppercase text-slate-500">Course Duration</label>
                      <input required value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none focus:border-blue-500" placeholder="e.g. 1 Year, 6 Months" />
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3">
                    <div>
                      <label className="mb-2 block text-xs font-black uppercase text-slate-500">Category</label>
                      <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none">
                        <option value="NEET">NEET</option><option value="JEE Main">JEE Main</option><option value="Boards + Foundation">Boards + Foundation</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-black uppercase text-slate-500">Base Price</label>
                      <input required value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none" placeholder="e.g. 4999" type="number" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-black uppercase text-slate-500">Status</label>
                      <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none">
                        <option value="Active">Active</option><option value="Draft">Draft</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
  <h3 className="mb-4 border-b pb-2 text-sm font-black uppercase tracking-widest text-blue-600">Card Display Settings</h3>
  <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
    <div>
      <label className="mb-2 block text-xs font-black uppercase text-slate-500">Level</label>
      <select value={formData.level} onChange={(e) => setFormData({ ...formData, level: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none focus:border-blue-500">
        <option value="Beginner">Beginner</option>
        <option value="Intermediate">Intermediate</option>
        <option value="Advanced">Advanced</option>
      </select>
    </div>
    <div>
      <label className="mb-2 block text-xs font-black uppercase text-slate-500">Badge Tag</label>
      <input value={formData.tag} onChange={(e) => setFormData({ ...formData, tag: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none focus:border-blue-500" placeholder="e.g. Popular, Foundation" />
    </div>
    <div>
       <label className="mb-2 block text-xs font-black uppercase text-slate-500">URL Slug</label>
       <input required value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none focus:border-blue-500" placeholder="e.g. neet-biology-mastery" />
    </div>
  </div>
  <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
      <div>
        <label className="mb-2 block text-xs font-black uppercase text-slate-500">SEO Title (Sub-heading)</label>
        <input required value={formData.seoTitle} onChange={(e) => setFormData({ ...formData, seoTitle: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none focus:border-blue-500" placeholder="Detailed Title..." />
      </div>
       <div>
        <label className="mb-2 block text-xs font-black uppercase text-slate-500">Target Audience</label>
        <input value={formData.audience} onChange={(e) => setFormData({ ...formData, audience: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none focus:border-blue-500" placeholder="Who is this for?" />
      </div>
  </div>
  <div className="mt-5">
      <label className="mb-2 block text-xs font-black uppercase text-slate-500">Short Description (For Card Preview)</label>
      <textarea required value={formData.short_desc} onChange={(e) => setFormData({ ...formData, short_desc: e.target.value })} rows={2} className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-bold outline-none focus:border-blue-500" placeholder="Brief 2-3 line summary..." />
  </div>
</div>

                {/* Rest of the Advanced Settings (Features & Plans) as it was */}
                <div>
                  <h3 className="mb-4 border-b pb-2 text-sm font-black uppercase tracking-widest text-blue-600">2. Description & Highlights</h3>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="mb-5 w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-bold outline-none" placeholder="Detailed syllabus or course notes..." />
                  
                  <div className="flex gap-2 mb-4">
                    <input value={newFeature} onChange={(e) => setNewFeature(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddFeature())} className="h-11 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none" placeholder="Add custom highlight..." />
                    <button type="button" onClick={handleAddFeature} className="rounded-xl bg-[#101827] px-5 text-sm font-black text-white">Add</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {features.map((feature, index) => (
                      <span key={index} className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-700">{feature} <button type="button" onClick={() => handleRemoveFeature(index)} className="font-bold text-red-500">&times;</button></span>
                    ))}
                  </div>
                </div>

               <h3 className="mb-4 border-b pb-2 text-sm font-black uppercase text-blue-600">3. Course Materials (Static)</h3>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-xs font-black uppercase text-slate-600">Thumbnail Image</label>
                    <input type="file" accept="image/*" onChange={(e) => setFiles({...files, image: e.target.files?.[0] || null})} className="w-full text-xs" />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-black uppercase text-slate-600">Promo Video</label>
                    <input type="file" accept="video/*" onChange={(e) => setFiles({...files, video: e.target.files?.[0] || null})} className="w-full text-xs" />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-black uppercase text-slate-600">Syllabus PDF</label>
                    <input type="file" accept="application/pdf" onChange={(e) => setFiles({...files, pdf: e.target.files?.[0] || null})} className="w-full text-xs" />
                  </div>
                </div>
             </form>
             <div className="flex justify-end gap-4 border-t bg-white p-6">
              <button type="button" onClick={() => setIsModalOpen(false)} className="rounded-2xl px-6 py-3 text-sm font-black text-slate-600 hover:bg-slate-100">Cancel</button>
              <button disabled={isSubmitting} form="courseForm" type="submit" className="rounded-2xl bg-[#101827] px-8 py-3 text-sm font-black text-white hover:bg-blue-600 disabled:opacity-50">
                {isSubmitting ? "Saving Course..." : "Publish Course Outline"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const MiniInfo = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4"><p className="text-xs font-bold uppercase text-slate-400">{label}</p><p className="mt-1 truncate text-base font-black text-slate-900">{value}</p></div>
);
export default AdminCourses;